import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import {
  AdminTools,
  Divider,
  CollapsibleSection,
  CollapsibleHeader,
  PaginationControls,
  PaginationLabel,
  PaginationButton,
} from './AllTabFilters.styled';

import { TicketStatusFilter } from './components/TicketStatusFilter';
import { SortOrderFilter } from './components/SortOrderFilter';
import { IdentifierSearchSection } from './components/IdentifierSearchSection';
import DateRangeTimePicker from '../../common/DateRangeTimePicker/DateRangeTimePicker';
import { BusinessFilterSection, BrandFilterSection } from './components/BusinessFilterSection';
import { FilterActions } from './components/FilterActions';
import { TicketFiltersSection } from './components/TicketFiltersSection';

import { useBusinessFilters } from './hooks/useBusinessFilters';
import { useFilterTouchedState } from './hooks/useFilterTouchedState';
import { useFilterUrlSync } from './hooks/useFilterUrlSync';

import { IDENTIFIER_TYPES } from '../../../constants/identifierTypes';
import { DEFAULT_IDENTIFIER_TYPE } from '../../../constants/identifierTypes';
import { TICKET_STATUS_FILTER } from '../../../constants/ticketStatuses';
import { CONVERSATION_QUERY_LIMIT as RESULTS_PER_PAGE } from '../../../constants/limits';

import {
  changeDateRangeForAllFilter,
  changeTicketStatusForAllFilter,
  changeSkipForAllFilter,
  changePaginationForAllFilter,
  changeIdentifierForAllFilter,
  changePartialMatchForAllFilter,
  changeSelectedBusinessesForAllFilter,
  changeSelectedBusinessBrandOptionsForAllFilter,
  changeExcludeBusinessesForAllFilter,
  changeSortOrderForAllFilter,
  filterAllTickets,
  setAllTabSearchPerformed,
  syncAllFiltersToUrl,
} from '../../../actions/allFilterActions';
import { businessesDetailsPageLoad } from '../../../actions/businessActions';

import {
  getBusinessesNamesAndIds,
  getDateRange,
  getNumTicketsForAllTab,
  getPaginationSkip,
  getSelectedBusinessIdList,
  getBusinessBrandOptions,
  getBusinessNameAndIdById,
  businessDetailsNextPageExist as selectBusinessDetailsNextPageExist,
  getPaginationForAllTab,
  getNewPaginationInfoForAllTab,
  getIdentifier,
  getAllFilterPartialMatch,
  getSelectedBusinessBrandOptionsList,
} from '../../../extractFromState';

const AllTabFilters = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const selectedTicketStatus = useSelector((state) => state.allFilters?.ticketStatus) || 'ALL';
  const selectedSortOrder = useSelector((state) => state.allFilters?.sortOrder) || null;
  const businessNames = useSelector(getBusinessesNamesAndIds);
  const selectedBusinessIdList = useSelector(getSelectedBusinessIdList);
  const businessBrandOptions = useSelector(getBusinessBrandOptions);
  const selectedBusinessBrandOptionsList = useSelector(getSelectedBusinessBrandOptionsList);
  const dateRange = useSelector(getDateRange);
  const businessDetailsNextPageExist = useSelector(selectBusinessDetailsNextPageExist);
  const identifier = useSelector(getIdentifier);
  const partialMatch = useSelector(getAllFilterPartialMatch);
  const skip = useSelector(getPaginationSkip);
  const totalTicketCount = useSelector(getNumTicketsForAllTab);
  const pagination = useSelector(getPaginationForAllTab);
  const newPageInfo = useSelector(getNewPaginationInfoForAllTab, shallowEqual);
  const selectedBusinessList = useSelector((state) =>
    selectedBusinessIdList.map((id) => getBusinessNameAndIdById(state, id))
  );

  const [showContentSearch, setShowContentSearch] = useState(false);
  const [showTicketFilters, setShowTicketFilters] = useState(true);

  // Local state for identifier input (not dispatched until search click)
  const [localIdentifierType, setLocalIdentifierType] = useState(identifier.type || DEFAULT_IDENTIFIER_TYPE);
  const [localIdentifierValue, setLocalIdentifierValue] = useState(identifier.value || '');

  // Sync local identifier state when Redux changes (e.g. from URL init)
  useEffect(() => {
    setLocalIdentifierType(identifier.type || DEFAULT_IDENTIFIER_TYPE);
    setLocalIdentifierValue(identifier.value || '');
  }, [identifier.type, identifier.value]);

  const { touchedFields, isSearchPending, markSearched } = useFilterTouchedState({
    localIdentifierType,
    localIdentifierValue,
  });

  const { isReady, initialUrlHadFilters } = useFilterUrlSync();

  // NOTE(vlad|SPD-14360|2026-03-05): Auto-trigger search when URL has filter params on page load
  useEffect(() => {
    if (isReady && initialUrlHadFilters) {
      markSearched();
      dispatch(setAllTabSearchPerformed(true));
      filterTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, initialUrlHadFilters]);

  useEffect(() => {
    dispatch(changePaginationForAllFilter({ ...newPageInfo }));
  }, [newPageInfo, dispatch]);

  const filterTickets = useCallback(
    async (filterParams) => {
      await dispatch(filterAllTickets(filterParams));
    },
    [dispatch]
  );

  const handlePartialMatchChange = useCallback(
    (event) => {
      dispatch(changePartialMatchForAllFilter(event.target.checked));
    },
    [dispatch]
  );

  const businessFilters = useBusinessFilters({
    businessBrandOptions,
    selectedBusinessBrandOptionsList,
  });

  const handleTicketStatusSelect = useCallback(
    (ticketStatusOption) => {
      dispatch(changeTicketStatusForAllFilter(ticketStatusOption));
    },
    [dispatch]
  );

  const handleSortOrderSelect = useCallback(
    (sortOrderOption) => {
      dispatch(changeSortOrderForAllFilter(sortOrderOption));
    },
    [dispatch]
  );

  const onDateRangeFilterDatesChangedHandler = useCallback(
    ({ startDate: start, endDate: end, startTime: startT, endTime: endT }) => {
      dispatch(
        changeDateRangeForAllFilter({
          startDate: start,
          endDate: end,
          startTime: startT,
          endTime: endT,
        })
      );
    },
    [dispatch]
  );

  const handleIdentifierTypeChange = useCallback(
    (selectedId) => {
      setLocalIdentifierType(selectedId);
      setLocalIdentifierValue(
        selectedId === IDENTIFIER_TYPES.SIMPLR_TICKET && identifier.type === IDENTIFIER_TYPES.SIMPLR_TICKET
          ? identifier.value
          : ''
      );
    },
    [identifier]
  );

  const handleIdentifierValueChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalIdentifierValue(value);

      // When input is fully cleared, clear the identifier in Redux
      if (!value) {
        dispatch(changeIdentifierForAllFilter(localIdentifierType, ''));
      }
    },
    [localIdentifierType, dispatch]
  );

  const handleSearchClick = useCallback(() => {
    const trimmedValue = (localIdentifierValue || '').trim();

    if (trimmedValue) {
      dispatch(changeIdentifierForAllFilter(localIdentifierType, trimmedValue));
    } else {
      dispatch(changeIdentifierForAllFilter(localIdentifierType, ''));
    }

    markSearched();
    dispatch(setAllTabSearchPerformed(true));
    dispatch(syncAllFiltersToUrl(history));
    filterTickets();
  }, [localIdentifierValue, localIdentifierType, dispatch, filterTickets, history, markSearched]);

  const handleIdentifierKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleSearchClick();
      }
    },
    [handleSearchClick]
  );

  const handleContentSearchToggle = useCallback(() => {
    setShowContentSearch((prev) => !prev);
  }, []);

  // NOTE(vlad|SPD-14444|2026-03-12): Reset clears filter UI only — does NOT re-search or update URL.
  // URL mirrors loaded conversations; conversations stay visible (hasSearched stays true).
  const clearAllFiltersButtonHandler = useCallback(() => {
    dispatch(changeTicketStatusForAllFilter(TICKET_STATUS_FILTER.all));
    dispatch(changeSortOrderForAllFilter(null));
    dispatch(changeDateRangeForAllFilter({ startDate: null, endDate: null, startTime: null, endTime: null }));
    dispatch(changeIdentifierForAllFilter(DEFAULT_IDENTIFIER_TYPE, ''));
    dispatch(changePartialMatchForAllFilter(false));
    dispatch(changeSelectedBusinessesForAllFilter([]));
    dispatch(changeSelectedBusinessBrandOptionsForAllFilter([]));
    dispatch(changeExcludeBusinessesForAllFilter(false));
    setLocalIdentifierType(DEFAULT_IDENTIFIER_TYPE);
    setLocalIdentifierValue('');
  }, [dispatch]);

  const handleNextPageClick = useCallback(async () => {
    const newSkip = skip + RESULTS_PER_PAGE;
    const { nextPageCursor } = pagination;
    await filterTickets({ skip: newSkip, cursor: nextPageCursor });
    dispatch(changeSkipForAllFilter(newSkip));
  }, [skip, pagination, filterTickets, dispatch]);

  const handlePreviousPageClick = useCallback(async () => {
    const newSkip = Math.max(skip - RESULTS_PER_PAGE, 0);
    const { previousPageCursor } = pagination;
    await filterTickets({ skip: newSkip, isPrev: true, cursor: previousPageCursor });
    dispatch(changeSkipForAllFilter(newSkip));
  }, [skip, pagination, filterTickets, dispatch]);

  const onToggleTicketFiltersHandler = useCallback(() => {
    setShowTicketFilters((prev) => !prev);
  }, []);

  return (
    <Divider>
      <AdminTools>
        <TicketFiltersSection
          touchedFields={touchedFields}
          isExpanded={showTicketFilters}
          onToggle={onToggleTicketFiltersHandler}
          statusFilter={
            <TicketStatusFilter selectedStatus={selectedTicketStatus} onSelect={handleTicketStatusSelect} />
          }
          sortFilter={<SortOrderFilter selectedSortOrder={selectedSortOrder} onSelect={handleSortOrderSelect} />}
          dateRangeFilter={
            <DateRangeTimePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              startTime={dateRange.startTime}
              endTime={dateRange.endTime}
              onDatesChange={onDateRangeFilterDatesChangedHandler}
            />
          }
          showBusinessFilter={businessFilters.showBusinessFilter}
          businessFilter={
            <BusinessFilterSection
              selectedBusinessList={selectedBusinessList}
              businessNames={businessNames}
              isLoading={false}
              hasNextPage={businessDetailsNextPageExist}
              onBusinessInputChange={businessFilters.onBusinessSelectInputChange}
              onBusinessLoad={() => dispatch(businessesDetailsPageLoad())}
              onBusinessChange={businessFilters.onBusinessSelectFilterValuesChangedHandler}
            />
          }
          brandFilter={
            <BrandFilterSection
              selectedBusinessIdList={selectedBusinessIdList}
              selectedBrandOptions={selectedBusinessBrandOptionsList}
              brandOptions={businessFilters.formatBusinessBrandOptionsNames()}
              onBrandInputChange={businessFilters.onBusinessBrandOptionsSelectInputChange}
              onBusinessLoad={() => dispatch(businessesDetailsPageLoad())}
              onBrandChange={businessFilters.onBusinessBrandOptionsSelectFilterValuesChangedHandler}
              hasNextPage={businessDetailsNextPageExist}
              isLoading={false}
            />
          }
        />

        <IdentifierSearchSection
          touchedFields={touchedFields}
          selectedIdentifierType={localIdentifierType}
          identifierValue={localIdentifierValue}
          partialMatch={partialMatch}
          onIdentifierTypeChange={handleIdentifierTypeChange}
          onIdentifierValueChange={handleIdentifierValueChange}
          onIdentifierKeyDown={handleIdentifierKeyDown}
          onPartialMatchChange={handlePartialMatchChange}
        />

        {/* NOTE(vlad|SPD-14090|2026-02-11): Content search block temporarily hidden until SPD-14101 implementation.
            This section will include entity search functionality for message content, labels, etc.
            Design: https://www.figma.com/design/9QFW3lSsTduEVZG5kKrq01/Expert-Platform-Exploration?node-id=3338-10611 */}
        {false && (
          <CollapsibleSection>
            <CollapsibleHeader onClick={handleContentSearchToggle}>
              <span>Content search</span>
              <img
                src={
                  showContentSearch
                    ? '/images/new-designs/chevron-up-blue.svg'
                    : '/images/new-designs/chevron-down-blue.svg'
                }
                alt=""
              />
            </CollapsibleHeader>
            {showContentSearch && (
              <div style={{ padding: '10px 0', color: '#8793AA', fontSize: '11px' }}>
                Content search functionality coming soon...
              </div>
            )}
          </CollapsibleSection>
        )}

        <FilterActions
          isSearchPending={isSearchPending}
          onReset={clearAllFiltersButtonHandler}
          onSearch={handleSearchClick}
          pagination={
            <PaginationControls>
              <PaginationButton
                className={`glyphicon glyphicon-triangle-left ${pagination?.hasPreviousPage ? '' : 'hide'}`}
                title="Previous"
                onClick={handlePreviousPageClick}
              />
              <PaginationLabel>
                Results{' '}
                {totalTicketCount > 0 ? `${skip + 1} - ${Math.min(RESULTS_PER_PAGE + skip, totalTicketCount)}` : 0}
              </PaginationLabel>
              <PaginationButton
                className={`glyphicon glyphicon-triangle-right ${pagination?.hasNextPage ? '' : 'hide'}`}
                title="Next"
                onClick={handleNextPageClick}
              />
            </PaginationControls>
          }
        />
      </AdminTools>
    </Divider>
  );
};

export { AllTabFilters };

export default AllTabFilters;
